import { Helmet } from "react-helmet";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { companyInfo, organizationSchema, localBusinessSchema } from "../config/seoConfig";
import { pageSeoData, seoImage } from "../config/pageSeoConfig";
import { staticPortfolios, staticReviews } from "../config/staticData";
import { 
  FaArrowRight, 
  FaCode, 
  FaMobile, 
  FaPalette, 
  FaShoppingCart,
  FaRocket,
  FaUsers,
  FaAward,
  FaProjectDiagram,
  FaQuoteLeft,
  FaStar,
  FaPlay,
  FaCheck,
  FaSearch,
  FaHeadset,
  FaReact,
  FaNodeJs,
  FaJs,
  FaPython,
  FaPhp,
  FaAws,
  FaDocker,
  FaGitAlt,
  FaCloud,
  FaCogs,
  FaBullhorn,
  FaExternalLinkAlt,
  FaGithub,
  FaDatabase,
  FaLinkedin,
  FaFacebook,
  FaWhatsapp,
  FaEnvelope,
  FaChartLine,
  FaDownload,
  FaArrowUp,
  FaShieldAlt,
  FaClock,
  FaDollarSign,
  FaLaptopCode
} from "react-icons/fa";
import { 
  SiMongodb, 
  SiMysql, 
  SiTypescript, 
  SiNextdotjs, 
  SiVuedotjs,
  SiAngular,
  SiLaravel,
  SiFirebase,
  SiRedis,
  SiKubernetes,
  SiPython,
  SiPostgresql,
  SiGraphql
} from "react-icons/si";
import { IconType } from "react-icons";
import "./Home.scss";

interface ServiceType {
  icon: IconType;
  title: string;
  description: string;
  features: string[];
  link: string;
}

interface WhyChooseUsType {
  icon: IconType;
  title: string;
  description: string;
  position: "left" | "center" | "right" | "bottom";
  highlight?: string;
  highlightLabel?: string;
}

interface StatType {
  number: string;
  label: string;
  icon: IconType;
}

interface PortfolioType {
  id: number;
  title: string;
  slug: string;
  category: string;
  image: string;
  description: string;
  technologies: string[];
  liveUrl?: string;
  githubUrl?: string;
  featured: boolean;
}

interface ReviewType {
  id: number;
  name: string;
  company: string;
  position: string;
  rating: number;
  review: string;
  image?: string;
  createdAt: string;
}

const Home: React.FC = () => {
  // State for dynamic data
  const [portfolios, setPortfolios] = useState<PortfolioType[]>([]);
  const [reviews, setReviews] = useState<ReviewType[]>([]);
  const [loadingPortfolios, setLoadingPortfolios] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);

  // Typewriter effect state
  const [typedText, setTypedText] = useState("");
  const roles = [
    "Web/Mobile Apps Development Expert",
    "Software Development Specialist",
    "Full Stack Developer",
    "Cloud Architect",
    "DevOps Engineer"
  ];
  const [currentRole, setCurrentRole] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Scroll direction state
  const [showScrollUp, setShowScrollUp] = useState(false);

  // Typewriter effect
  useEffect(() => {
    const typeSpeed = isDeleting ? 50 : 100;
    const currentText = roles[currentRole];
    
    const timer = setTimeout(() => {
      if (!isDeleting && typedText === currentText) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && typedText === "") {
        setIsDeleting(false);
        setCurrentRole((prev) => (prev + 1) % roles.length);
      } else {
        setTypedText(
          isDeleting
            ? currentText.substring(0, typedText.length - 1)
            : currentText.substring(0, typedText.length + 1)
        );
      }
    }, typeSpeed);

    return () => clearTimeout(timer);
  }, [typedText, isDeleting, currentRole]);

  // Scroll detection for scroll indicator
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show scroll up when scrolled past 50% of the page
      if (scrollPosition > windowHeight * 0.5) {
        setShowScrollUp(true);
      } else {
        setShowScrollUp(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Set page title from config
  useEffect(() => {
    document.title = pageSeoData.home.title;
  }, []);

  // Use static data instead of API calls
  useEffect(() => {
    // Set only FEATURED portfolios (limit to 3)
    const featuredPortfolios = staticPortfolios.filter(p => p.featured).slice(0, 3);
    setPortfolios(featuredPortfolios);
    setLoadingPortfolios(false);
  }, []);

  useEffect(() => {
    // Set only FIRST 3 reviews for home page
    setReviews(staticReviews.slice(0, 3));
    setLoadingReviews(false);
  }, []);

  // SEO meta data from config
  const seoTitle = pageSeoData.home.title;
  const seoDescription = pageSeoData.home.description;
  const seoKeywords = pageSeoData.home.keywords;
  const seoUrl = pageSeoData.home.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      organizationSchema,
      localBusinessSchema,
      {
        "@type": "WebSite",
        "@id": `${companyInfo.url}#website`,
        "url": companyInfo.url,
        "name": companyInfo.name,
        "description": seoDescription,
        "publisher": {
          "@id": `${companyInfo.url}#organization`
        },
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": `${companyInfo.url}#webpage`,
        "url": seoUrl,
        "name": seoTitle,
        "description": seoDescription,
        "isPartOf": {
          "@id": `${companyInfo.url}#website`
        },
        "about": {
          "@id": `${companyInfo.url}#organization`
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": seoImage
        },
        "datePublished": "2020-01-01T00:00:00+00:00",
        "dateModified": "2025-11-01T00:00:00+00:00",
        "inLanguage": "en-US"
      }
    ]
  };
  // Icon components for TypeScript compatibility
  const ArrowRightIcon = FaArrowRight;
  const PlayIcon = FaPlay;
  const CheckIcon = FaCheck;
  const QuoteLeftIcon = FaQuoteLeft;
  const StarIcon = FaStar;

  // Hero section data
  const heroStats = [
    { icon: <FaRocket />, value: "150+", label: "Projects Completed" },
    { icon: <FaUsers />, value: "95%", label: "Client Satisfaction" },
    { icon: <FaChartLine />, value: "10+", label: "Years Experience" },
    { icon: <FaStar />, value: "4.9/5", label: "Average Rating" },
  ];

  const skills = [
    { icon: <FaReact />, name: "React", color: "#61DAFB" },
    { icon: <FaNodeJs />, name: "Node.js", color: "#339933" },
    { icon: <SiTypescript />, name: "TypeScript", color: "#3178C6" },
    { icon: <SiPython />, name: "Python", color: "#3776AB" },
    { icon: <SiMongodb />, name: "MongoDB", color: "#47A248" },
    { icon: <SiPostgresql />, name: "PostgreSQL", color: "#4169E1" },
    { icon: <FaAws />, name: "AWS", color: "#FF9900" },
    { icon: <FaDocker />, name: "Docker", color: "#2496ED" },
    { icon: <SiKubernetes />, name: "Kubernetes", color: "#326CE5" },
    { icon: <SiGraphql />, name: "GraphQL", color: "#E10098" },
    { icon: <SiNextdotjs />, name: "Next.js", color: "#000000" },
    { icon: <FaDatabase />, name: "SQL", color: "#CC2927" },
  ];

  const socialLinks = [
    { icon: <FaGithub />, url: "https://github.com", label: "GitHub", color: "#181717" },
    { icon: <FaLinkedin />, url: "https://linkedin.com", label: "LinkedIn", color: "#0A66C2" },
    { icon: <FaFacebook />, url: "https://facebook.com", label: "Facebook", color: "#1877F2" },
    { icon: <FaWhatsapp />, url: "https://wa.me/918826360188", label: "WhatsApp", color: "#25D366" },
  ];

  const services: ServiceType[] = [
    {
      icon: FaProjectDiagram,
      title: "Web Application",
      description: "Scalable web applications with advanced features for complex business requirements.",
      features: ["Progressive Web Apps", "Real-time Features", "Cloud Integration", "Performance Optimization"],
      link: "/services/web-application-development"
    },
     {
      icon: FaMobile,
      title: "Mobile Application",
      description: "Native and cross-platform mobile applications for iOS and Android platforms.",
      features: ["React Native", "Flutter", "iOS/Android Native", "App Store Publishing"],
      link: "/services/mobile-app-development"
    },
    {
      icon: FaShoppingCart,
      title: "E-commerce Solutions",
      description: "Complete online stores with payment processing, inventory management, and analytics.",
      features: ["Shopping Cart", "Payment Gateway", "Inventory Management", "Order Processing"],
      link: "/services/ecommerce-development"
    },
    {
      icon: FaCode,
      title: "Web Design & Development",
      description: "Custom websites and web applications built with cutting-edge technologies and best practices.",
      features: ["React/Angular/Vue", "Node.js/PHP", "Database Integration", "API Development"],
      link: "/services/web-development"
    },
    {
      icon: FaPalette,
      title: "UI/UX Design",
      description: "Create exceptional user experiences with modern design principles and user-centered approach.",
      features: ["User Research", "Wireframing", "Prototyping", "Design Systems"],
      link: "/services/ui-ux-design"
    },
    
    {
      icon: FaCloud,
      title: "Cloud Solutions",
      description: "Scalable cloud infrastructure and migration services for modern applications.",
      features: ["Cloud Migration", "AWS/Azure/GCP", "Auto Scaling", "Security & Compliance"],
      link: "/services/cloud-solutions"
    },
    {
      icon: FaCogs,
      title: "DevOps",
      description: "Automated CI/CD pipelines and infrastructure management for efficient development.",
      features: ["CI/CD Pipelines", "Containerization", "Monitoring", "Infrastructure as Code"],
      link: "/services/devops"
    },
    {
      icon: FaBullhorn,
      title: "Digital Marketing",
      description: "Strategic digital marketing campaigns to boost your online presence and drive growth.",
      features: ["SEO Optimization", "Social Media Marketing", "Google Ads", "Content Marketing"],
      link: "/services/digital-marketing"
    }
  ];

  const whyChooseUs: WhyChooseUsType[] = [
    {
      icon: FaCode,
      title: "Custom App Development",
      description: "App Developer Pro builds high-performance, custom application solutions from scratch using modern technologies like React, Angular, and Vue.js to meet your unique business requirements.",
      position: "left"
    },
    {
      icon: FaMobile,
      title: "Mobile-First Design",
      description: "Highly ensure that every application build by app developer pro is crafted with a fully responsive, mobile-first approach - delivering seamless performance, flawless layouts, and an exceptional user experience on every device, screen size, and platform.",
      position: "left"
    },
    {
      icon: FaRocket,
      title: "Performance Focused",
      description: "Every applications developed by App Developer Pro is consistently deliver 95%+ performance scores, ultra-fast loading speeds, and SEO-optimized architecture that boosts your visibility and rankings. With smooth interactions, intuitive navigation, and a frictionless user experience, App Developer Pro transform casual visitors into loyal, high-value customers - helping your business grow faster and smarter.",
      highlight: "95%",
      highlightLabel: "Performance Score",
      position: "center"
    },
    {
      icon: FaSearch,
      title: "SEO Optimized",
      description: "App Developer Pro is the well knowing name for high traffic generating built-in search engine optimization strategies that help your applications rank higher on Google, drive organic traffic, and increase your online visibility.",
      position: "right"
    },
    {
      icon: FaHeadset,
      title: "Ongoing Support",
      description: "App developer pro is always with you for comprehensive maintenance, security updates, and ongoing support to keep your applications running smoothly with 24/7 monitoring and assistance.",
      position: "right"
    },
    {
      icon: FaShieldAlt,
      title: "Enterprise Security",
      description: "Bank-grade security with SSL encryption, JWT authentication, and role-based access control to protect your data and users.",
      position: "bottom"
    },
    {
      icon: FaClock,
      title: "On-Time Delivery",
      description: "Transparent communication, agile methodology, and strict adherence to deadlines ensure your project launches on schedule.",
      position: "bottom"
    },
    {
      icon: FaDollarSign,
      title: "Cost-Effective Solutions",
      description: "Premium quality development at competitive rates with transparent pricing and no hidden costs or surprises.",
      position: "bottom"
    },
    {
      icon: FaChartLine,
      title: "Scalable Architecture",
      description: "Future-proof solutions built with microservices and cloud infrastructure that grow seamlessly with your business needs.",
      position: "bottom"
    }
  ];

  const stats: StatType[] = [
    { number: "150+", label: "Projects Completed", icon: FaProjectDiagram },
    { number: "100+", label: "Happy Clients", icon: FaUsers },
    { number: "10+", label: "Years Experience", icon: FaAward },
    { number: "24/7", label: "Support", icon: FaRocket }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:site_name" content="App Developer Pro" />
        <meta property="og:image" content={seoImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="App Developer Pro - Web/Mobile Apps & Software Development " />
        <meta property="og:locale" content="en_US" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@appdeveloperpro" />
        <meta name="twitter:creator" content="@appdeveloperpro" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:image:alt" content="App Developer Pro - Web/Mobile Apps & Software Development" />
        {/* JSON-LD Schema */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="home">
      {/* Hero Section */}
      <section 
        className="hero-section"
        style={{ backgroundImage: "url(/images/team-at-work-app-developer-pro.jpg)" }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="service-tags">
              <span className="tag">
                <FaMobile /> Mobile Apps
              </span>
              <span className="tag">
                <FaLaptopCode /> Web Apps
              </span>
              <span className="tag">
                <FaPalette /> UI/UX Design
              </span>
              <span className="tag">
                <FaCogs /> Dev Ops
              </span>
              <span className="tag">
                <FaCloud /> Cloud Solutions
              </span>
            </div>
            <h2 className="hero-pretitle">"Crafting Intelligent, Cloud-Ready Digital Experiences"</h2>
            <h1 className="hero-title">Custom Web/Mobile App & Software Development Solutions</h1>
            <p className="hero-subtitle">Powerful web/mobile application & software with digital solutions that drive business growth. Your vision, our expertise - together we build innovative digital products.</p>

            {/* CTA Buttons with Micro-interactions */}
            <motion.div
              className="hero-cta-group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/contact" className="cta-button cta-button--primary">
                <FaRocket /> LET"S BUILD TOGETHER
              </Link>
              <Link to="/portfolio" className="cta-button cta-button--secondary">
                <FaProjectDiagram /> VIEW PORTFOLIO
              </Link>
            </motion.div>

            {/* Compact Stats Integration - Inline Badges */}
            <motion.div
              className="hero-stats-badges"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <motion.div 
                className="hero-stat-badge"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="hero-stat-badge-icon">
                  <FaAward />
                </div>
                <div className="hero-stat-badge-content">
                  <span className="hero-stat-badge-number">10+</span>
                  <span className="hero-stat-badge-label">Years Expertise</span>
                </div>
              </motion.div>

              <motion.div 
                className="hero-stat-badge"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="hero-stat-badge-icon">
                  <FaUsers />
                </div>
                <div className="hero-stat-badge-content">
                  <span className="hero-stat-badge-number">50+</span>
                  <span className="hero-stat-badge-label">Global Clients</span>
                </div>
              </motion.div>

              <motion.div 
                className="hero-stat-badge"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="hero-stat-badge-icon">
                  <FaChartLine />
                </div>
                <div className="hero-stat-badge-content">
                  <span className="hero-stat-badge-number">10+</span>
                  <span className="hero-stat-badge-label">Industries</span>
                </div>
              </motion.div>

              <motion.div 
                className="hero-stat-badge"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="hero-stat-badge-icon">
                  <FaRocket />
                </div>
                <div className="hero-stat-badge-content">
                  <span className="hero-stat-badge-number">80+</span>
                  <span className="hero-stat-badge-label">Projects Done</span>
                </div>
              </motion.div>
            </motion.div>

            {/* 3D Floating Elements for Depth */}
            <div className="hero-floating-elements">
              <motion.div 
                className="hero-float-element hero-float-element--1"
                animate={{ 
                  y: [0, -20, 0],
                  rotate: [0, 5, 0]
                }}
                transition={{ 
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <FaCode />
              </motion.div>
              <motion.div 
                className="hero-float-element hero-float-element--2"
                animate={{ 
                  y: [0, 20, 0],
                  rotate: [0, -5, 0]
                }}
                transition={{ 
                  duration: 7,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <FaMobile />
              </motion.div>
              <motion.div 
                className="hero-float-element hero-float-element--3"
                animate={{ 
                  y: [0, -15, 0],
                  rotate: [0, 10, 0]
                }}
                transition={{ 
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <FaCloud />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="home__services">
        <div className="home__section-header">
          <motion.h2
            className="home__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            High Performance Web/Mobile Application & Software Development Solutions for Modern Businesses
          </motion.h2>
          <h3 className="home__section-subtitle">
           Reach customers around the world via custom web/mobile apps & software development services in USA, India, Germany, Spain & UK.
          </h3>
          <motion.p
            className="home__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
           
          </motion.p>
          <p>In today's fast-paced digital world, businesses need more than just software - they need intelligent, scalable, and future-ready web & mobile applications that drive growth and innovation. At app developer pro, our end-to-end high performance, custom web/mobile apps & software development solutions empower organizations to streamline operations, enhance customer engagement, and stay ahead of the competition. App developer pro innovate your project by precision & deep understanding of modern business needs. The mission of App Developer Pro is simple - to help businesses leverage technology to achieve sustainable success. From crafting seamless user experiences to building robust back-end architectures, we ensure every solution we deliver is performance-driven, secure, and scalable.</p>
        </div>
        <div className="home__services-grid">
          {services.map((service, index) => {
            const ServiceIcon = service.icon;
            return (
              <motion.div
                key={service.title}
                className="home__service-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="home__service-header">
                  <div className="home__service-icon">
                    <ServiceIcon />
                  </div>
                  <h3 className="home__service-title">{service.title}</h3>
                </div>
              <p className="home__service-description">{service.description}</p>
              <ul className="home__service-features">
                {service.features.map((feature) => (
                  <li key={feature} className="home__service-feature">
                    <CheckIcon /> {feature}
                  </li>
                ))}
              </ul>
              <Link to={service.link} className="home__service-link">
                Learn More <ArrowRightIcon />
              </Link>
            </motion.div>
            );
          })}
        </div>
      </section>
 {/* Why Choose Us Section */}
      <section className="home__why-choose-us">
        <div className="home__why-choose-us-content">
          <div className="home__section-header">
            <motion.h2
              className="home__section-title"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              Why Choose App Developer Pro ?
            </motion.h2>
            <h3 className="home__section-subtitle">Don't Just Build Apps, Build Results | Expert Solutions. Proven Results. Trusted by Businesses. Driven by Innovation </h3>
            <p>App Developer Pro doesn't just develop applications - we build digital experiences that accelerate business growth digitally. App Developer Pro blends modern design, powerful technology, and smart automation to deliver apps that are fast, reliable, and ready to scale. Whether you're a startup or an established brand, This is the right place to transform your ideas into high-performance web/mobile application & software solutions that drive real business results. With transparent communication, on-time delivery, and after-launched dedicated support, App Developer Pro makes your digital journey seamless from concept to launch and beyond.</p>
          </div>
          <div className="home__why-choose-us-grid">
          {/* Left Column */}
          <div className="home__why-choose-us-column home__why-choose-us-column--left">
            {whyChooseUs.filter(item => item.position === "left").map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="home__why-choose-us-card"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="home__why-choose-us-header">
                    <div className="home__why-choose-us-icon">
                      <ItemIcon />
                    </div>
                    <h3 className="home__why-choose-us-title">{item.title}</h3>
                  </div>
                  <p className="home__why-choose-us-description">{item.description}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Center Column */}
          <div className="home__why-choose-us-column home__why-choose-us-column--center">
            {whyChooseUs.filter(item => item.position === "center").map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="home__why-choose-us-card home__why-choose-us-card--center"
                  style={{
                    backgroundImage: `linear-gradient(135deg, rgba(14, 165, 233, 0.92) 0%, rgba(30, 58, 95, 0.95) 100%), url("/images/app-developer-pro-full-stack-expert.jpg")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundBlendMode: "overlay"
                  }}
                  aria-label="Full stack developer working on high-performance applications"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="home__why-choose-us-header">
                    <div className="home__why-choose-us-icon">
                      <ItemIcon />
                    </div>
                    <h3 className="home__why-choose-us-title">{item.title}</h3>
                  </div>
                  <p className="home__why-choose-us-description">{item.description}</p>
                  {item.highlight && (
                    <div className="home__why-choose-us-highlight">
                      <div className="home__why-choose-us-number">{item.highlight}</div>
                      <div className="home__why-choose-us-label">{item.highlightLabel}</div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Right Column */}
          <div className="home__why-choose-us-column home__why-choose-us-column--right">
            {whyChooseUs.filter(item => item.position === "right").map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="home__why-choose-us-card"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="home__why-choose-us-header">
                    <div className="home__why-choose-us-icon">
                      <ItemIcon />
                    </div>
                    <h3 className="home__why-choose-us-title">{item.title}</h3>
                  </div>
                  <p className="home__why-choose-us-description">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
          </div>

          {/* Bottom Row - 3 Cards */}
          <div className="home__why-choose-us-bottom-row">
            {whyChooseUs.filter(item => item.position === "bottom").map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="home__why-choose-us-card home__why-choose-us-card--bottom"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                >
                  <div className="home__why-choose-us-header">
                    <div className="home__why-choose-us-icon">
                      <ItemIcon />
                    </div>
                    <h3 className="home__why-choose-us-title">{item.title}</h3>
                  </div>
                  <p className="home__why-choose-us-description">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
          
          {/* Free Consultation Button */}
          <motion.div
            className="home__why-choose-us-cta"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Link to="/contact" className="home__why-choose-us-btn">
              Free Consultation <ArrowRightIcon />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Technologies We Use Section */}
      <section className="home__technologies">
  <motion.div
    className="home__section-header"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
  >
    <h2 className="home__section-title">The Technologies, App Developer Pro Work With</h2>
    <h3 className="home__section-subtitle">
                 Future-Ready Tech for Scalable Solutions, Where Modern Technologies Meet Expert Development
    </h3>
    <p>
      App developer pro builds high-performance, scalable applications using React.js / Next.js for fast, modern frontends and Node.js or Python (FastAPI) for powerful, efficient backend services. My solutions run on PostgreSQL or MongoDB databases and are deployed on leading cloud platforms like AWS, Google Cloud, and Azure for reliability and seamless scaling. With a microservices architecture powered by REST and GraphQL APIs, every system is modular and future-ready. To ensure maximum protection, we implement JWT authentication, SSL security, and role-based access control, delivering secure and enterprise-grade digital solutions.
    </p>
  </motion.div>
  
  <div className="home__technologies-carousel">
    <div className="home__technologies-track">
      {[
        { icon: FaReact, name: "React", color: "#61DAFB" },
        { icon: FaNodeJs, name: "Node.js", color: "#339933" },
        { icon: SiTypescript, name: "TypeScript", color: "#3178C6" },
        { icon: FaJs, name: "JavaScript", color: "#F7DF1E" },
        { icon: SiMongodb, name: "MongoDB", color: "#47A248" },
        { icon: SiMysql, name: "MySQL", color: "#4479A1" },
        { icon: SiNextdotjs, name: "Next.js", color: "#000000" },
        { icon: SiVuedotjs, name: "Vue.js", color: "#4FC08D" },
        { icon: SiAngular, name: "Angular", color: "#DD0031" },
        { icon: FaPython, name: "Python", color: "#3776AB" },
        { icon: FaPhp, name: "PHP", color: "#777BB4" },
        { icon: SiLaravel, name: "Laravel", color: "#FF2D20" },
        { icon: FaAws, name: "AWS", color: "#232F3E" },
        { icon: FaDocker, name: "Docker", color: "#2496ED" },
        { icon: SiFirebase, name: "Firebase", color: "#FFCA28" },
        { icon: FaGitAlt, name: "Git", color: "#F05032" },
        { icon: SiRedis, name: "Redis", color: "#DC382D" },
        { icon: SiKubernetes, name: "Kubernetes", color: "#326CE5" }
      ].concat([
        { icon: FaReact, name: "React", color: "#61DAFB" },
        { icon: FaNodeJs, name: "Node.js", color: "#339933" },
        { icon: SiTypescript, name: "TypeScript", color: "#3178C6" },
        { icon: FaJs, name: "JavaScript", color: "#F7DF1E" },
        { icon: SiMongodb, name: "MongoDB", color: "#47A248" },
        { icon: SiMysql, name: "MySQL", color: "#4479A1" }
      ]).map((tech, index) => {
        const IconComponent = tech.icon;
        return (
          <motion.div
            key={`${tech.name}-${index}`}
            className="home__technology-item"
            whileHover={{ scale: 1.1, y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <div 
              className="home__technology-icon"
              style={{ "--tech-color": tech.color } as React.CSSProperties}
            >
              <IconComponent />
            </div>
            <span className="home__technology-name">{tech.name}</span>
          </motion.div>
        );
      })}
    </div>
  </div>
</section>

     
      {/* Stats Section */}
      <section className="home__stats">
        <div className="home__stats-grid">
          {stats.map((stat, index) => {
            const StatIcon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                className="home__stat-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="home__stat-header">
                  <div className="home__stat-icon">
                    <StatIcon />
                  </div>
                  <div className="home__stat-content">
                    <div className="home__stat-number">{stat.number}</div>
                    <div className="home__stat-label">{stat.label}</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="home__portfolio">
        <div className="home__section-header">
          <motion.h2
            className="home__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Featured Work
          </motion.h2>
          <motion.p
            className="home__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Showcasing our latest projects and digital innovations
          </motion.p>
        </div>
        {loadingPortfolios ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p style={{ fontSize: "18px", color: "#6c757d" }}>Loading portfolios...</p>
          </div>
        ) : portfolios.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p style={{ fontSize: "18px", color: "#6c757d" }}>No featured portfolios available yet.</p>
          </div>
        ) : (
          <div className="home__portfolio-grid">
            {portfolios.map((project, index) => (
              <motion.div
                key={project.id}
                className="home__portfolio-item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="home__portfolio-image">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                  />
                  <div className="home__portfolio-overlay">
                    <div className="home__portfolio-links">
                      {project.liveUrl && (
                        <a 
                          href={project.liveUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="home__portfolio-link-btn"
                          title="View Live Project"
                        >
                          <FaExternalLinkAlt /> Live
                        </a>
                      )}
                      {project.githubUrl && (
                        <a 
                          href={project.githubUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="home__portfolio-link-btn"
                          title="View on GitHub"
                        >
                          <FaGithub /> GitHub
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="home__portfolio-content">
                  <div className="home__portfolio-header">
                    <span className="home__portfolio-category">{project.category}</span>
                    <h3 className="home__portfolio-title">{project.title}</h3>
                  </div>
                  <p className="home__portfolio-description">{project.description}</p>
                  <div className="home__portfolio-technologies">
                    {project.technologies.map((tech) => (
                      <span key={tech} className="home__portfolio-tech">{tech}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        <div className="home__portfolio-cta">
          <Link to="/portfolio" className="home__cta-btn">
            View All Projects <ArrowRightIcon />
          </Link>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="home__testimonials">
        <div className="home__section-header">
          <motion.h2
            className="home__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            What Our Clients Say
          </motion.h2>
        </div>
        {loadingReviews ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p style={{ fontSize: "18px", color: "#6c757d" }}>Loading reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p style={{ fontSize: "18px", color: "#6c757d" }}>No reviews available yet.</p>
          </div>
        ) : (
          <div className="home__testimonials-grid">
            {reviews.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="home__testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="home__testimonial-quote">
                  <QuoteLeftIcon />
                </div>
                <div className="home__testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} />
                  ))}
                </div>
                <p className="home__testimonial-text">"{testimonial.review}"</p>
                <div className="home__testimonial-author">
                  <img 
                    src={testimonial.image || "/api/placeholder/60/60"} 
                    alt={testimonial.name} 
                  />
                  <div>
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.position} at {testimonial.company}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

     

      {/* CTA Section */}
      <section className="home__cta">
        <video 
          className="home__cta-video"
          autoPlay 
          loop 
          muted 
          playsInline
        >
          <source src="/images/app-developer-cta.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="home__cta-overlay"></div>
        <motion.div
          className="home__cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="home__cta-title">Ready to Build Your Next Application?</h2>
          <p className="home__cta-text">
            Let"s bring your app ideas to life. Contact us today for a free consultation 
            and discover how our app development expertise can transform your business.
          </p>
          <div className="home__cta-buttons">
            <Link to="/contact" className="home__cta-btn home__cta-btn--primary">
              Get Free Quote <ArrowRightIcon />
            </Link>
            <Link to="/portfolio" className="home__cta-btn home__cta-btn--secondary">
              View Our Work
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
    </>
  );
};

export default Home;

